/**
 * The settings the office's exe applies WITHOUT a restart — and so the SuperAdmin feature switches
 * that must be followed by "Refresh Exe Settings" (2026-09-24).
 *
 * Every other feature switch is read by the cloud or the app and takes effect at once. These two
 * are read by the Teamio application on the practice's own server, which only re-reads them when:
 *   - SuperAdmin sends "Refresh Exe Settings" (exe 3.0.56+) — within seconds, or
 *   - its own 10-minute re-read comes round (exe 3.0.56+), or
 *   - the application restarts (any version — "Restart App").
 * So flipping one of these and walking away leaves the practice on the old setting for up to ten
 * minutes on a current exe, and INDEFINITELY on an older one. SuperAdmin shows a reminder.
 *
 * ⛔ CONTRACT — the same list lives in:
 *   teamio-exe    main-process/app/live-settings.js   (what the exe re-reads, both branches)
 *   teamio-admin  lib/exe-settings-refresh.js          (what the command is sent for + the version gate)
 * cypress/node-tests/exe-live-settings-superadmin.test.js compares them.
 */

export const EXE_LIVE_SETTINGS = Object.freeze(['SCHEDULE_VIEW_ENABLED', 'PRACTICE_PULSE_PAID_FOR']);

/** First exe that handles the command. teamio-admin enforces it; this copy is for the wording. */
export const EXE_SETTINGS_MIN_VERSION = '3.0.56';

/** How the reminder names each setting — the words on its SuperAdmin card. */
export const EXE_LIVE_SETTING_LABELS = Object.freeze({
  SCHEDULE_VIEW_ENABLED: 'Schedule - Chart Audit',
  PRACTICE_PULSE_PAID_FOR: 'Practice Ops',
});

/** Does changing this setting need the exe told? */
export function needsExeRefresh(property) {
  return EXE_LIVE_SETTINGS.indexOf(property) !== -1;
}

/**
 * Add a changed setting to the reminder's list (no duplicates, card order). Pure: returns a new list.
 */
export function addPendingExeSetting(pending, property) {
  if (!needsExeRefresh(property)) return Array.isArray(pending) ? pending.slice() : [];
  const set = new Set(Array.isArray(pending) ? pending : []);
  set.add(property);
  return EXE_LIVE_SETTINGS.filter(key => set.has(key));
}

export function describePendingExeSettings(pending) {
  return (Array.isArray(pending) ? pending : [])
    .map(key => EXE_LIVE_SETTING_LABELS[key] || key)
    .join(' and ');
}
